import { useState, useEffect, useRef } from 'react'
import { Container, CircularProgress, Switch } from '@mui/material'
import Spacebox from '../components/Spacebox'
import Flexbox from '../components/Flexbox'
import { useSelector } from 'react-redux'

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']

const INITIAL_FORM = {
    firstname: '', lastname: '', email: '', phone: '', state: 'TX',
    debt_amount: '', call_type: 'outbound', buyer: '',
    date_of_birth: '', address: '', city: '', zipcode: '',
    isSCP: true, unsecured_debt_amount: ''
}

function Label({ children }) {
    return (
        <div style={{ fontSize: '11px', opacity: .5, fontWeight: 700, marginBottom: 4, letterSpacing: '.5px' }}>
            {children}
        </div>
    )
}

function ResultItem({ label, value, mono }) {
    if (!value) return null
    return (
        <div>
            <div style={{ fontSize: '11px', opacity: .5, fontWeight: 600, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '14px', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>
                {value}
            </div>
        </div>
    )
}

function OutboundResult({ data, dark }) {
    return (
        <div style={{ border: '1px solid #86efac', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 20px', background: dark ? '#052e16' : '#f0fdf4', borderBottom: '1px solid #86efac' }}>
                <Flexbox alignItems="center" style={{ gap: 8 }}>
                    <span style={{ fontSize: '18px' }}>✅</span>
                    <span style={{ fontWeight: 700, color: dark ? '#86efac' : '#15803d' }}>Outbound Call Initiated</span>
                </Flexbox>
            </div>
            <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <ResultItem label="Call ID" value={data?.call_id} mono />
                    <ResultItem label="Status" value={data?.call_status} />
                    <ResultItem label="From" value={data?.from_number} />
                    <ResultItem label="To" value={data?.to_number} />
                    <ResultItem label="Agent ID" value={data?.agent_id} mono />
                    <ResultItem label="Direction" value={data?.direction} />
                </div>
            </div>
        </div>
    )
}

function InboundResult({ data, dark }) {
    const [copied, setCopied] = useState(null)

    const copy = (num) => {
        navigator.clipboard.writeText(num).then(() => {
            setCopied(num)
            setTimeout(() => setCopied(null), 2000)
        })
    }

    return (
        <div style={{ border: '1px solid #93c5fd', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 20px', background: dark ? '#0c1a2e' : '#eff6ff', borderBottom: '1px solid #93c5fd' }}>
                <Flexbox alignItems="center" style={{ gap: 8 }}>
                    <span style={{ fontSize: '18px' }}>📞</span>
                    <span style={{ fontWeight: 700, color: dark ? '#93c5fd' : '#1d4ed8' }}>Inbound — Dial the Agent</span>
                </Flexbox>
            </div>
            <div style={{ padding: '16px 20px' }}>
                <ResultItem label="Agent" value={data?.agent_name} />
                <Spacebox padding="12px" />
                <Label>DIAL ONE OF THESE NUMBERS — click to copy</Label>
                {(data?.agent_numbers ?? []).map((num, i) => (
                    <div
                        key={i}
                        onClick={() => copy(num)}
                        style={{
                            fontFamily: 'monospace', fontSize: '20px', fontWeight: 700,
                            padding: '10px 0',
                            borderBottom: i < data.agent_numbers.length - 1 ? `1px solid ${dark ? '#1e2a3a' : '#e2e8f0'}` : 'none',
                            cursor: 'pointer', userSelect: 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ color: copied === num ? '#22c55e' : 'inherit', transition: 'color .2s' }}>
                            {copied === num ? '✓ Copied!' : num}
                        </span>
                        <span style={{ fontSize: '11px', fontFamily: 'sans-serif', fontWeight: 600, opacity: .4, marginLeft: 12 }}>
                            {copied === num ? '' : 'COPY'}
                        </span>
                    </div>
                ))}
                {(!data?.agent_numbers || data.agent_numbers.length === 0) && (
                    <small style={{ opacity: .5 }}>No numbers found for this agent.</small>
                )}
            </div>
        </div>
    )
}

const TestEnvironment = ({ title }) => {
    document.querySelector('title').innerHTML = title

    const mode = useSelector(state => state.darkmode.value)
    const shade = mode.dark ? '#111' : '#f9fafb'
    const card = mode.dark ? '#1e1e1e' : '#ffffff'
    const border = mode.dark ? '#2e2e2e' : '#e4e6eb'
    const text = mode.dark ? '#f0f0f0' : '#111111'

    const [form, setForm] = useState(INITIAL_FORM)
    const [showOptional, setShowOptional] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')

    const [disposition, setDisposition] = useState(null)
    const [polling, setPolling] = useState(false)
    const [pollAttempt, setPollAttempt] = useState(0)
    const [pollExhausted, setPollExhausted] = useState(false)
    const [checkingManual, setCheckingManual] = useState(false)
    const pollRef = useRef(null)
    const attemptRef = useRef(0)
    const MAX_POLL = 5

    const stopPolling = () => {
        if (pollRef.current) clearInterval(pollRef.current)
        pollRef.current = null
    }

    const fetchDisposition = async (email) => {
        const res = await fetch(`/api/users/email/retell?email=${encodeURIComponent(email)}`, { credentials: 'include' })
        const json = await res.json()
        return json.success ? json.data?.disposition : null
    }

    const startPolling = (email) => {
        stopPolling()
        attemptRef.current = 0
        setPollAttempt(0)
        setDisposition(null)
        setPollExhausted(false)
        setPolling(true)

        pollRef.current = setInterval(async () => {
            attemptRef.current += 1
            setPollAttempt(attemptRef.current)

            try {
                const dispo = await fetchDisposition(email)
                if (dispo) {
                    setDisposition(dispo)
                    stopPolling()
                    setPolling(false)
                    return
                }
            } catch (_) {}

            if (attemptRef.current >= MAX_POLL) {
                stopPolling()
                setPolling(false)
                setPollExhausted(true)
            }
        }, 60_000)
    }

    const handleManualCheck = async () => {
        setCheckingManual(true)
        try {
            const dispo = await fetchDisposition(form.email)
            if (dispo) {
                setDisposition(dispo)
                setPollExhausted(false)
            }
        } catch (_) {}
        setCheckingManual(false)
    }

    useEffect(() => () => stopPolling(), [])

    const set = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }))
        setResult(null)
        setError('')
    }

    const inputStyle = {
        width: '100%', padding: '10px 14px', borderRadius: 8,
        border: `1px solid ${border}`, background: shade,
        fontSize: '14px', outline: 'none', color: text,
        boxSizing: 'border-box'
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        stopPolling()
        setSubmitting(true)
        setError('')
        setResult(null)
        setDisposition(null)
        setPollExhausted(false)
        setPollAttempt(0)
        setPolling(false)
        try {
            const payload = {
                firstname: form.firstname,
                lastname: form.lastname,
                email: form.email,
                phone: form.phone,
                state: form.state,
                debt_amount: Number(form.debt_amount),
                call_type: form.call_type,
                isSCP: form.isSCP,
                unsecured_debt_amount: form.isSCP ? Number(form.unsecured_debt_amount) : null,
                ...(form.buyer && { buyer: form.buyer }),
                ...(form.date_of_birth && { date_of_birth: form.date_of_birth }),
                ...(form.address && { address: form.address }),
                ...(form.city && { city: form.city }),
                ...(form.zipcode && { zipcode: form.zipcode })
            }
            const res = await fetch('/api/env/create-test-user', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include'
            })
            const json = await res.json()
            if (!json.success) throw new Error(json.msg || 'Request failed')
            setResult({ type: form.call_type, data: json.data })
            startPolling(form.email)
        } catch (err) {
            setError(err.message)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div style={{ minHeight: '100dvh', paddingBottom: 60 }}>
            <Container maxWidth="sm">
                <Spacebox padding="24px" />
                <p style={{ fontWeight: 700, fontSize: '20px', margin: 0 }}>Test Environment</p>
                <small style={{ opacity: .5 }}>Create a test user and trigger a Retell call.</small>
                <Spacebox padding="24px" />

                <form onSubmit={handleSubmit}>

                    {/* ── Lead Info ── */}
                    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: '20px' }}>
                        <Label>LEAD INFO</Label>
                        <Spacebox padding="14px" />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                            <div>
                                <Label>FIRST NAME *</Label>
                                <input style={inputStyle} value={form.firstname} onChange={e => set('firstname', e.target.value)} placeholder="John" required />
                            </div>
                            <div>
                                <Label>LAST NAME *</Label>
                                <input style={inputStyle} value={form.lastname} onChange={e => set('lastname', e.target.value)} placeholder="Doe" required />
                            </div>
                            <div>
                                <Label>EMAIL *</Label>
                                <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="john@test.com" required />
                            </div>
                            <div>
                                <Label>PHONE *</Label>
                                <input style={inputStyle} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="2025551234" required />
                            </div>
                            <div>
                                <Label>STATE *</Label>
                                <select style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)} required>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>DECLARED DEBT ($) *</Label>
                                <input style={inputStyle} type="number" min="0" value={form.debt_amount} onChange={e => set('debt_amount', e.target.value)} placeholder="25000" required />
                            </div>
                        </div>
                    </div>

                    <Spacebox padding="14px" />

                    {/* ── Soft Credit Pull ── */}
                    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: '20px' }}>
                        <Flexbox alignItems="center" style={{ gap: 4 }}>
                            <Switch
                                checked={form.isSCP}
                                onChange={e => set('isSCP', true)} // auto set to true
                                size="small"
                            />
                            <label style={{ cursor: 'pointer', fontWeight: 600, fontSize: '14px' }} onClick={() => set('isSCP', !form.isSCP)}>
                                Soft Credit Pull (isSCP)
                            </label>
                        </Flexbox>
                        <small style={{ opacity: .45, marginTop: 6, display: 'block', paddingLeft: 28 }}>
                            Simulates Array credit pull. Routes lead through SCP agent flow.
                        </small>

                        {form.isSCP && (
                            <>
                                <Spacebox padding="16px" />
                                <Label>UNSECURED DEBT AMOUNT ($) *</Label>
                                <input
                                    style={inputStyle}
                                    type="number"
                                    min="0"
                                    value={form.unsecured_debt_amount}
                                    onChange={e => set('unsecured_debt_amount', e.target.value)}
                                    placeholder="22000"
                                    required={form.isSCP}
                                />
                                <small style={{ opacity: .4, marginTop: 6, display: 'block' }}>
                                    ≥$20k → over20 tier &nbsp;·&nbsp; $10k–$19.9k → under20 tier &nbsp;·&nbsp; &lt;$10k → under7 tier
                                </small>
                            </>
                        )}
                    </div>

                    <Spacebox padding="14px" />

                    {/* ── Call Type ── */}
                    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: '20px' }}>
                        <Label>CALL TYPE</Label>
                        <Spacebox padding="14px" />
                        <Flexbox style={{ gap: 10 }}>
                            {['outbound', 'inbound'].map(t => (
                                <div
                                    key={t}
                                    onClick={() => set('call_type', t)}
                                    style={{
                                        flex: 1, padding: '14px', borderRadius: 10, cursor: 'pointer',
                                        border: `2px solid ${form.call_type === t ? '#0067CF' : border}`,
                                        background: form.call_type === t ? '#0067CF12' : 'transparent',
                                        textAlign: 'center', transition: 'all .15s', userSelect: 'none'
                                    }}
                                >
                                    <div style={{ fontWeight: 700, fontSize: '14px', textTransform: 'capitalize' }}>{t}</div>
                                    <div style={{ fontSize: '12px', opacity: .5, marginTop: 4 }}>
                                        {t === 'outbound' ? 'Server dials the lead' : 'Returns numbers to call in'}
                                    </div>
                                </div>
                            ))}
                        </Flexbox>
                    </div>

                    <Spacebox padding="14px" />

                    {/* ── Optional Fields ── */}
                    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
                        <button
                            type="button"
                            onClick={() => setShowOptional(p => !p)}
                            style={{
                                width: '100%', padding: '16px 20px', background: 'none', border: 'none',
                                cursor: 'pointer', display: 'flex', alignItems: 'center',
                                justifyContent: 'space-between', color: text, fontSize: '11px',
                                fontWeight: 700, letterSpacing: '.5px'
                            }}
                        >
                            <span>OPTIONAL FIELDS</span>
                            <span style={{ opacity: .45, fontSize: '20px', lineHeight: 1 }}>{showOptional ? '−' : '+'}</span>
                        </button>
                        {showOptional && (
                            <div style={{ padding: '4px 20px 20px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                    <div>
                                        <Label>BUYER</Label>
                                        <input style={inputStyle} value={form.buyer} onChange={e => set('buyer', e.target.value)} placeholder="National Debt Relief" />
                                    </div>
                                    <div>
                                        <Label>DATE OF BIRTH</Label>
                                        <input style={inputStyle} type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} />
                                    </div>
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <Label>ADDRESS</Label>
                                        <input style={inputStyle} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St" />
                                    </div>
                                    <div>
                                        <Label>CITY</Label>
                                        <input style={inputStyle} value={form.city} onChange={e => set('city', e.target.value)} placeholder="Austin" />
                                    </div>
                                    <div>
                                        <Label>ZIP CODE</Label>
                                        <input style={inputStyle} value={form.zipcode} onChange={e => set('zipcode', e.target.value)} placeholder="78701" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <Spacebox padding="20px" />

                    {/* ── Error ── */}
                    {error && (
                        <div style={{
                            borderRadius: 8, padding: '12px 16px', marginBottom: 14,
                            background: mode.dark ? '#2d0a0a' : '#fef2f2',
                            border: `1px solid ${mode.dark ? '#7f1d1d' : '#fca5a5'}`
                        }}>
                            <small style={{ color: mode.dark ? '#fca5a5' : '#dc2626', fontWeight: 600 }}>{error}</small>
                        </div>
                    )}

                    {/* ── Submit ── */}
                    <button
                        type="submit"
                        disabled={submitting}
                        style={{
                            width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                            background: '#0067CF', color: 'white', fontWeight: 700, fontSize: '15px',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                            opacity: submitting ? .7 : 1, transition: 'opacity .2s'
                        }}
                    >
                        {submitting && <CircularProgress size={16} style={{ color: 'white' }} />}
                        {submitting
                            ? 'Creating test user…'
                            : `Create User & ${form.call_type === 'outbound' ? 'Trigger Outbound Call' : 'Get Inbound Numbers'}`}
                    </button>
                </form>

                {/* ── Result ── */}
                {result && (
                    <>
                        <Spacebox padding="20px" />
                        {result.type === 'outbound'
                            ? <OutboundResult data={result.data} dark={mode.dark} />
                            : <InboundResult data={result.data} dark={mode.dark} />}
                    </>
                )}

                {/* ── Disposition ── */}
                {result && (
                    <>
                        <Spacebox padding="14px" />

                        {/* Found */}
                        {disposition && (
                            <div style={{ border: '1px solid #a3e635', borderRadius: 12, overflow: 'hidden' }}>
                                <div style={{ padding: '12px 20px', background: mode.dark ? '#1a2e05' : '#f7fee7', borderBottom: '1px solid #a3e635' }}>
                                    <Flexbox alignItems="center" style={{ gap: 8 }}>
                                        <span style={{ fontSize: '16px' }}>🏷️</span>
                                        <span style={{ fontWeight: 700, color: mode.dark ? '#a3e635' : '#3f6212', fontSize: '13px' }}>CALL DISPOSITION</span>
                                    </Flexbox>
                                </div>
                                <div style={{ padding: '14px 20px' }}>
                                    <p style={{ margin: 0, fontWeight: 600, fontSize: '15px' }}>{disposition}</p>
                                </div>
                            </div>
                        )}

                        {/* Polling */}
                        {polling && !disposition && (
                            <div style={{
                                border: `1px solid ${border}`, borderRadius: 12,
                                padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14
                            }}>
                                <CircularProgress size={16} />
                                <div>
                                    <p style={{ margin: 0, fontWeight: 600, fontSize: '14px' }}>Waiting for disposition…</p>
                                    <small style={{ opacity: .5 }}>
                                        Attempt {pollAttempt} of {MAX_POLL} · checks every 60s
                                    </small>
                                </div>
                            </div>
                        )}

                        {/* Exhausted — manual fallback */}
                        {pollExhausted && !disposition && (
                            <div style={{ border: '1px solid #fcd34d', borderRadius: 12, overflow: 'hidden' }}>
                                <div style={{ padding: '12px 20px', background: mode.dark ? '#2d1f00' : '#fffbeb', borderBottom: '1px solid #fcd34d' }}>
                                    <span style={{ fontWeight: 700, color: mode.dark ? '#fcd34d' : '#92400e', fontSize: '13px' }}>
                                        No disposition after 5 checks
                                    </span>
                                </div>
                                <div style={{ padding: '14px 20px' }}>
                                    <small style={{ opacity: .6, display: 'block', marginBottom: 12 }}>
                                        The call may still be in progress. Click below to check manually.
                                    </small>
                                    <button
                                        onClick={handleManualCheck}
                                        disabled={checkingManual}
                                        style={{
                                            padding: '9px 18px', borderRadius: 8, border: '1px solid #fcd34d',
                                            background: '#fef3c7', fontWeight: 600, fontSize: '13px',
                                            cursor: checkingManual ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            opacity: checkingManual ? .6 : 1
                                        }}
                                    >
                                        {checkingManual && <CircularProgress size={13} />}
                                        {checkingManual ? 'Checking…' : 'Check Disposition Now'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <Spacebox padding="40px" />
            </Container>
        </div>
    )
}

export default TestEnvironment
